import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, GitBranch, Minimize2, Lock, Timer, KeyRound, Boxes, ScrollText, UserCheck, Info } from "lucide-react";
import PageHero from "../components/common/PageHero";
import { Reveal, SectionHeading } from "../components/common/Section";
import { Button } from "../components/ui/button";
import { SECURITY_ITEMS } from "../data/content";

const icons = [GitBranch, Minimize2, Lock, Timer, KeyRound, Boxes, ScrollText, UserCheck];

export default function SecurityGovernance() {
  useEffect(() => {
    document.title = "Security & Governance | norse3";
  }, []);

  return (
    <div>
      <PageHero
        eyebrow="Security & governance"
        title="How data flows, and where humans stay in control."
        subtitle="We explain data handling only where it is supported. Where something is not yet confirmed, we say so rather than overstate it."
      >
        <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Link to="/book-a-demo">Book a walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
        </Button>
      </PageHero>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Controls"
              title="Data handling, access and oversight."
              subtitle="Configurable to your policy. Customers remain responsible for policy and final decisions."
            />
          </Reveal>
          <Reveal className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" delay={100}>
            {SECURITY_ITEMS.map((s, i) => {
              const Icon = icons[i];
              return (
                <div key={s.title} className="rounded-xl border border-white/10 bg-navy-900/60 p-6">
                  <Icon className="h-5 w-5 text-primary" />
                  <h3 className="mt-4 text-base font-semibold text-foreground">{s.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                </div>
              );
            })}
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-2 gap-10">
          <Reveal>
            <SectionHeading
              eyebrow="Governance foundations"
              title="Quality and AI management, honestly framed."
            />
            <div className="mt-6 space-y-4">
              <div className="rounded-xl border border-white/10 bg-navy-900/60 p-5">
                <p className="text-sm font-semibold text-foreground">ISO 9001 &amp; ISO 42001</p>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  We operate quality-management and AI-management approaches aligned to these
                  standards. We display verified scope, certifier and validity only once
                  confirmed — we do not present unverified badges.
                </p>
              </div>
              <div className="rounded-xl border border-white/10 bg-navy-900/60 p-5">
                <p className="text-sm font-semibold text-foreground">SOC 2</p>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  SOC 2 readiness is a roadmap item, not a certification. We will not claim it
                  until it is achieved and verifiable.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="rounded-lg border border-primary/25 bg-primary/[0.06] p-7 h-full">
              <div className="flex items-center gap-2">
                <UserCheck className="h-5 w-5 text-primary" />
                <p className="text-sm font-semibold text-foreground">Accountability stays with you</p>
              </div>
              <p className="mt-4 text-muted-foreground leading-relaxed">
                norse3 supports alignment and is designed to reduce risk. It does not make
                final decisions, approve outcomes or accept accountability on your behalf.
              </p>
              <ul className="mt-5 space-y-2.5 text-sm text-foreground">
                <li className="flex items-start gap-2"><span className="text-primary">•</span> You define the policy.</li>
                <li className="flex items-start gap-2"><span className="text-primary">•</span> You configure the actions.</li>
                <li className="flex items-start gap-2"><span className="text-primary">•</span> Your reviewers make the calls.</li>
                <li className="flex items-start gap-2"><span className="text-primary">•</span> norse3 records the evidence.</li>
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-14">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="flex items-start gap-3 rounded-xl border border-white/10 bg-navy-900/60 p-5">
            <Info className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
            <p className="text-sm text-muted-foreground">
              Some deployment specifics depend on the option you choose and your
              environment. Where a detail is not yet confirmed for your context, we will
              tell you rather than overstate our position.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
